import {
  GRANTABLE_BITS,
  GRANTABLE_PERMISSIONS,
  GRANTABLE_PERMISSION_NAMES,
  PERMISSION_ADMINISTRATOR,
  PERMISSION_MANAGE_GUILD,
  PERMISSION_MANAGE_ROLES,
  PERMISSION_MANAGE_THREADS,
} from "@colossus/contracts";
import type { GrantablePermission } from "@colossus/contracts";
import { m } from "./paraglide/messages.js";
import type { Message } from "./locale.ts";

const SECTIONS = {
  general: {
    title: m.leveling_perm_section_general,
    permissions: {
      view_channel: [
        GRANTABLE_PERMISSIONS.view_channel,
        m.leveling_perm_view_channel,
        m.leveling_perm_view_channel_hint,
      ],
      manage_channels: [
        1n << 4n,
        m.leveling_perm_manage_channels,
        m.leveling_perm_manage_channels_hint,
      ],
      manage_roles: [
        PERMISSION_MANAGE_ROLES,
        m.leveling_perm_manage_roles,
        m.leveling_perm_manage_roles_hint,
      ],
      create_guild_expressions: [
        GRANTABLE_PERMISSIONS.create_guild_expressions,
        m.leveling_perm_create_guild_expressions,
        m.leveling_perm_create_guild_expressions_hint,
      ],
      manage_guild_expressions: [
        1n << 30n,
        m.leveling_perm_manage_guild_expressions,
        m.leveling_perm_manage_guild_expressions_hint,
      ],
      view_audit_log: [
        1n << 7n,
        m.leveling_perm_view_audit_log,
        m.leveling_perm_view_audit_log_hint,
      ],
      view_guild_insights: [
        1n << 19n,
        m.leveling_perm_view_guild_insights,
        m.leveling_perm_view_guild_insights_hint,
      ],
      manage_webhooks: [
        1n << 29n,
        m.leveling_perm_manage_webhooks,
        m.leveling_perm_manage_webhooks_hint,
      ],
      manage_guild: [
        PERMISSION_MANAGE_GUILD,
        m.leveling_perm_manage_guild,
        m.leveling_perm_manage_guild_hint,
      ],
    },
  },
  membership: {
    title: m.leveling_perm_section_membership,
    permissions: {
      create_instant_invite: [
        GRANTABLE_PERMISSIONS.create_instant_invite,
        m.leveling_perm_create_instant_invite,
        m.leveling_perm_create_instant_invite_hint,
      ],
      change_nickname: [
        GRANTABLE_PERMISSIONS.change_nickname,
        m.leveling_perm_change_nickname,
        m.leveling_perm_change_nickname_hint,
      ],
      manage_nicknames: [
        1n << 27n,
        m.leveling_perm_manage_nicknames,
        m.leveling_perm_manage_nicknames_hint,
      ],
      kick_members: [1n << 1n, m.leveling_perm_kick_members, m.leveling_perm_kick_members_hint],
      ban_members: [1n << 2n, m.leveling_perm_ban_members, m.leveling_perm_ban_members_hint],
      moderate_members: [
        1n << 40n,
        m.leveling_perm_moderate_members,
        m.leveling_perm_moderate_members_hint,
      ],
    },
  },
  text: {
    title: m.leveling_perm_section_text,
    permissions: {
      send_messages: [
        GRANTABLE_PERMISSIONS.send_messages,
        m.leveling_perm_send_messages,
        m.leveling_perm_send_messages_hint,
      ],
      send_messages_in_threads: [
        GRANTABLE_PERMISSIONS.send_messages_in_threads,
        m.leveling_perm_send_messages_in_threads,
        m.leveling_perm_send_messages_in_threads_hint,
      ],
      create_public_threads: [
        GRANTABLE_PERMISSIONS.create_public_threads,
        m.leveling_perm_create_public_threads,
        m.leveling_perm_create_public_threads_hint,
      ],
      create_private_threads: [
        GRANTABLE_PERMISSIONS.create_private_threads,
        m.leveling_perm_create_private_threads,
        m.leveling_perm_create_private_threads_hint,
      ],
      embed_links: [
        GRANTABLE_PERMISSIONS.embed_links,
        m.leveling_perm_embed_links,
        m.leveling_perm_embed_links_hint,
      ],
      attach_files: [
        GRANTABLE_PERMISSIONS.attach_files,
        m.leveling_perm_attach_files,
        m.leveling_perm_attach_files_hint,
      ],
      add_reactions: [
        GRANTABLE_PERMISSIONS.add_reactions,
        m.leveling_perm_add_reactions,
        m.leveling_perm_add_reactions_hint,
      ],
      use_external_emojis: [
        GRANTABLE_PERMISSIONS.use_external_emojis,
        m.leveling_perm_use_external_emojis,
        m.leveling_perm_use_external_emojis_hint,
      ],
      use_external_stickers: [
        GRANTABLE_PERMISSIONS.use_external_stickers,
        m.leveling_perm_use_external_stickers,
        m.leveling_perm_use_external_stickers_hint,
      ],
      mention_everyone: [
        1n << 17n,
        m.leveling_perm_mention_everyone,
        m.leveling_perm_mention_everyone_hint,
      ],
      manage_messages: [
        1n << 13n,
        m.leveling_perm_manage_messages,
        m.leveling_perm_manage_messages_hint,
      ],
      pin_messages: [1n << 51n, m.leveling_perm_pin_messages, m.leveling_perm_pin_messages_hint],
      bypass_slowmode: [
        1n << 52n,
        m.leveling_perm_bypass_slowmode,
        m.leveling_perm_bypass_slowmode_hint,
      ],
      manage_threads: [
        PERMISSION_MANAGE_THREADS,
        m.leveling_perm_manage_threads,
        m.leveling_perm_manage_threads_hint,
      ],
      read_message_history: [
        GRANTABLE_PERMISSIONS.read_message_history,
        m.leveling_perm_read_message_history,
        m.leveling_perm_read_message_history_hint,
      ],
      send_tts_messages: [
        GRANTABLE_PERMISSIONS.send_tts_messages,
        m.leveling_perm_send_tts_messages,
        m.leveling_perm_send_tts_messages_hint,
      ],
      send_voice_messages: [
        GRANTABLE_PERMISSIONS.send_voice_messages,
        m.leveling_perm_send_voice_messages,
        m.leveling_perm_send_voice_messages_hint,
      ],
      send_polls: [
        GRANTABLE_PERMISSIONS.send_polls,
        m.leveling_perm_send_polls,
        m.leveling_perm_send_polls_hint,
      ],
    },
  },
  voice: {
    title: m.leveling_perm_section_voice,
    permissions: {
      connect: [
        GRANTABLE_PERMISSIONS.connect,
        m.leveling_perm_connect,
        m.leveling_perm_connect_hint,
      ],
      speak: [GRANTABLE_PERMISSIONS.speak, m.leveling_perm_speak, m.leveling_perm_speak_hint],
      stream: [GRANTABLE_PERMISSIONS.stream, m.leveling_perm_stream, m.leveling_perm_stream_hint],
      use_soundboard: [
        GRANTABLE_PERMISSIONS.use_soundboard,
        m.leveling_perm_use_soundboard,
        m.leveling_perm_use_soundboard_hint,
      ],
      use_external_sounds: [
        GRANTABLE_PERMISSIONS.use_external_sounds,
        m.leveling_perm_use_external_sounds,
        m.leveling_perm_use_external_sounds_hint,
      ],
      use_vad: [
        GRANTABLE_PERMISSIONS.use_vad,
        m.leveling_perm_use_vad,
        m.leveling_perm_use_vad_hint,
      ],
      priority_speaker: [
        GRANTABLE_PERMISSIONS.priority_speaker,
        m.leveling_perm_priority_speaker,
        m.leveling_perm_priority_speaker_hint,
      ],
      mute_members: [1n << 22n, m.leveling_perm_mute_members, m.leveling_perm_mute_members_hint],
      deafen_members: [
        1n << 23n,
        m.leveling_perm_deafen_members,
        m.leveling_perm_deafen_members_hint,
      ],
      move_members: [1n << 24n, m.leveling_perm_move_members, m.leveling_perm_move_members_hint],
      set_voice_channel_status: [
        GRANTABLE_PERMISSIONS.set_voice_channel_status,
        m.leveling_perm_set_voice_channel_status,
        m.leveling_perm_set_voice_channel_status_hint,
      ],
    },
  },
  apps: {
    title: m.leveling_perm_section_apps,
    permissions: {
      use_application_commands: [
        GRANTABLE_PERMISSIONS.use_application_commands,
        m.leveling_perm_use_application_commands,
        m.leveling_perm_use_application_commands_hint,
      ],
      use_embedded_activities: [
        GRANTABLE_PERMISSIONS.use_embedded_activities,
        m.leveling_perm_use_embedded_activities,
        m.leveling_perm_use_embedded_activities_hint,
      ],
      use_external_apps: [
        GRANTABLE_PERMISSIONS.use_external_apps,
        m.leveling_perm_use_external_apps,
        m.leveling_perm_use_external_apps_hint,
      ],
    },
  },
  stage: {
    title: m.leveling_perm_section_stage,
    permissions: {
      request_to_speak: [
        GRANTABLE_PERMISSIONS.request_to_speak,
        m.leveling_perm_request_to_speak,
        m.leveling_perm_request_to_speak_hint,
      ],
    },
  },
  events: {
    title: m.leveling_perm_section_events,
    permissions: {
      create_events: [
        GRANTABLE_PERMISSIONS.create_events,
        m.leveling_perm_create_events,
        m.leveling_perm_create_events_hint,
      ],
      manage_events: [1n << 33n, m.leveling_perm_manage_events, m.leveling_perm_manage_events_hint],
    },
  },
  advanced: {
    title: m.leveling_perm_section_advanced,
    permissions: {
      administrator: [
        PERMISSION_ADMINISTRATOR,
        m.leveling_perm_administrator,
        m.leveling_perm_administrator_hint,
      ],
    },
  },
} satisfies Record<
  string,
  {
    title: Message;
    permissions: Record<string, readonly [bit: bigint, title: Message, description: Message]>;
  }
>;

type DiscordPermissionSection = keyof typeof SECTIONS;

const SECTION_ORDER = Object.keys(SECTIONS) as DiscordPermissionSection[];

interface DiscordRolePermission {
  readonly name: string;
  readonly bit: bigint;
  readonly section: DiscordPermissionSection;
  readonly title: Message;
  readonly description: Message;
}

export const DISCORD_ROLE_PERMISSIONS: readonly DiscordRolePermission[] = SECTION_ORDER.flatMap(
  (section) =>
    Object.entries(SECTIONS[section].permissions).map(([name, [bit, title, description]]) => ({
      name,
      bit,
      section,
      title,
      description,
    })),
);

export const isGrantable = (name: string): name is GrantablePermission =>
  (GRANTABLE_PERMISSION_NAMES as readonly string[]).includes(name);

const bitsBeyondPerks = (bitfield: string | null): bigint | null =>
  bitfield === null ? null : BigInt(bitfield) & ~GRANTABLE_BITS;

export type HeldPermission = Pick<DiscordRolePermission, "name" | "title">;

const OTHERS: HeldPermission = { name: "others", title: m.leveling_perm_others };

export const beyondPerks = (
  bitfield: string | null,
  everyone: string | null = null,
): readonly HeldPermission[] | null => {
  const beyond = bitsBeyondPerks(bitfield);
  if (beyond === null) return null;
  const bits = beyond & ~(everyone === null ? 0n : BigInt(everyone));
  const named = DISCORD_ROLE_PERMISSIONS.filter((permission) => (bits & permission.bit) !== 0n);
  const administrator = named.find((permission) => permission.name === "administrator");
  if (administrator !== undefined) return [administrator];
  const unnamed = named.reduce((rest, permission) => rest & ~permission.bit, bits);
  return unnamed === 0n ? named : [...named, OTHERS];
};

export const grantableWords = (names: readonly GrantablePermission[]): readonly HeldPermission[] =>
  DISCORD_ROLE_PERMISSIONS.filter((permission) =>
    (names as readonly string[]).includes(permission.name),
  );

export const permissionWords = (permissions: readonly HeldPermission[], locale: string): string =>
  new Intl.ListFormat(locale, { type: "conjunction" }).format(
    permissions.map((permission) => permission.title()),
  );

export const setPermission = (
  held: readonly GrantablePermission[],
  name: GrantablePermission,
  on: boolean,
): GrantablePermission[] =>
  GRANTABLE_PERMISSION_NAMES.filter((entry) => (entry === name ? on : held.includes(entry)));

interface PermissionSectionView {
  readonly section: DiscordPermissionSection;
  readonly title: Message;
  readonly permissions: readonly DiscordRolePermission[];
}

export const permissionSections = (
  query: string,
  locale: string,
): readonly PermissionSectionView[] => {
  const needle = query.trim().toLocaleLowerCase(locale);
  const matches = (permission: DiscordRolePermission): boolean =>
    needle === "" ||
    permission.title().toLocaleLowerCase(locale).includes(needle) ||
    permission.description().toLocaleLowerCase(locale).includes(needle);
  return SECTION_ORDER.map((section) => ({
    section,
    title: SECTIONS[section].title,
    permissions: DISCORD_ROLE_PERMISSIONS.filter(
      (permission) =>
        permission.section === section && isGrantable(permission.name) && matches(permission),
    ),
  })).filter((view) => view.permissions.length > 0);
};
